import { useEffect, useState } from "react";
import { History } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { InlineLoader } from "@/components/common/Loader";
import { OPS } from "@/constants/testIds";
import { fmt, fmtDateTime, labelOf } from "./coUtils";

const P = OPS.co.panel;
const FIELD_KEY = {
    invoice_no: "invoice", value_halalas: "value", occurred_at: "time", category: "category", reason_text: "reason",
    outcome: "outcome", outcome_note: "outcome_note", reissued_invoice_no: "reissued", source: "source",
    phone_kind: "customer", phone_masked: "customer", photo_key: "photo", duplicate_of: "duplicate",
};

function summary(e, t, lang, labels) {
    const b = e.details?.before || {};
    const a = e.details?.after || {};
    if (e.action === "co_photo_rejected") return null;
    const fields = [...new Set(Object.keys(FIELD_KEY)
        .filter((k) => JSON.stringify(b[k] ?? null) !== JSON.stringify(a[k] ?? null))
        .map((k) => t(`co_rec_field_${FIELD_KEY[k]}`)))];
    const bits = [];
    if (e.action !== "co_record_create" && fields.length) bits.push(fmt(t("co_rec_audit_changed"), { fields: fields.join("، ") }));
    const rs = a.review?.status;
    if (rs && rs !== b.review?.status) bits.push(`→ ${labelOf(labels?.review_statuses, rs, lang)}`);
    if (a.pos_match && a.pos_match !== b.pos_match) bits.push(`→ ${t(`co_rec_pos_${a.pos_match}`)}`);
    return bits.join(" · ") || null;
}

/** The record's trail from `GET /co/audit?record_id=` (masked snapshots only). */
export function RecordAudit({ recordId, reloadKey, labels }) {
    const { t, lang } = useI18n();
    const [rows, setRows] = useState(null);

    useEffect(() => {
        let alive = true;
        api.get("/co/audit", { params: { record_id: recordId, limit: 50 } })
            .then((r) => { if (alive) setRows(r.data.items || []); })
            .catch(() => { if (alive) setRows([]); });
        return () => { alive = false; };
    }, [recordId, reloadKey]);

    return (
        <section data-testid={P.audit} className="space-y-2">
            <h4 className="flex items-center gap-1.5 text-xs font-bold text-[color:var(--ink-muted)]">
                <History className="h-3.5 w-3.5" />{t("co_rec_audit_title")}
            </h4>
            {rows === null ? <InlineLoader /> : rows.length ? (
                <ol className="space-y-2 border-s-2 border-[color:var(--border-soft)] ps-3">
                    {rows.map((e) => {
                        const label = t(`co_audit_${e.action}`);
                        const n = e.details?.after?.rejected_count;
                        const line = summary(e, t, lang, labels);
                        return (
                            <li key={e.id} data-testid={P.auditRow(e.id)} data-action={e.action} className="text-xs">
                                <p className="font-semibold text-[color:var(--ink)]">
                                    {label === `co_audit_${e.action}` ? e.action : fmt(label, { n })}
                                </p>
                                <p className="text-[11px] text-[color:var(--ink-faint)]">
                                    {e.actor_name || t("co_rec_audit_system")} · {fmtDateTime(e.timestamp, lang)}
                                </p>
                                {line ? <p className="text-[11px] text-[color:var(--ink-muted)]">{line}</p> : null}
                            </li>
                        );
                    })}
                </ol>
            ) : (
                <p className="text-xs text-[color:var(--ink-faint)]">{t("co_rec_audit_empty")}</p>
            )}
        </section>
    );
}
